import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './PartnerMeetingDisplay.css';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';

/** Ensure date is YYYY-MM-DD for API (input type="date" already returns this; use when sending to backend) */
function toYYYYMMDD(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}


const DEFAULT_PARTNERS = [
  'LAXMIKANTH CHINCHURE',
  'GURURAJ CHINCHURE',
  'SHRISHAIL MANTAGI',
  'AKASH BIRAJDAR',
  'MALLIKARJUN CHINCHURE',
  'SHARANU KUMBAR',
  'SIDDARAM DHAGE',
  'VAIJUNATH KORE',
  'GURUSHANTH WADAGAON',
  'MAHESH HADAPAD',
  'SADANAND KARAGIR',
  'GURU BANDE',
];

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const PartnerMeetingDisplay = () => {
  const [availablePartners, setAvailablePartners] = useState(DEFAULT_PARTNERS);
  const [startDate, setStartDate] = useState(getTodayYYYYMMDD());
  const [endDate, setEndDate] = useState(getTodayYYYYMMDD());
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const partnersList = useMemo(() => {
    const deduped = new Set();
    return availablePartners
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name) => {
        if (!name) {
          return false;
        }
        const key = name.toUpperCase();
        if (deduped.has(key)) {
          return false;
        }
        deduped.add(key);
        return true;
      });
  }, [availablePartners]);

  const fetchData = useCallback(async (partnersOverride) => {
    setLoading(true);
    setError('');

    const partnersToFetch = Array.isArray(partnersOverride) ? partnersOverride : partnersList;

    if (partnersToFetch.length === 0) {
      setLoading(false);
      setError('Please enter at least one partner name.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/fetch-data`,
        {
          partners: partnersToFetch,
          startDate: toYYYYMMDD(startDate),
          endDate: toYYYYMMDD(endDate),
        },
        { timeout: 60000 }
      );

      setTableData(response.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      const message =
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch data. Please try again.';
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Check that the backend is running and try again.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [partnersList, startDate, endDate]);

  useEffect(() => {
    let isMounted = true;

    const loadPartners = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/partners`);
        const partnerList = Array.isArray(response.data)
          ? response.data.map((name) => (typeof name === 'string' ? name.trim() : '')).filter(Boolean)
          : [];

        if (!isMounted || partnerList.length === 0) {
          if (!isMounted) {
            return;
          }
          setAvailablePartners(DEFAULT_PARTNERS);
          return;
        }

        setAvailablePartners(partnerList);
      } catch (err) {
        console.error('Error loading partner names:', err);
        if (!isMounted) {
          return;
        }
        setAvailablePartners(DEFAULT_PARTNERS);
      }
    };

    loadPartners();

    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  // Note: fetch is now manual via the button to avoid continuous auto-loading

  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateRowTotal = (row) =>
    toNumber(row.od) +
    toNumber(row.gl) +
    toNumber(row.cd) +
    toNumber(row.tbd) +
    toNumber(row.dc);

  const basePartners = useMemo(() => {
    if (partnersList.length > 0) {
      return partnersList;
    }
    if (availablePartners.length > 0) {
      return availablePartners;
    }
    return DEFAULT_PARTNERS;
  }, [partnersList, availablePartners]);

  const displayRows = useMemo(() => {
    const partnerKeys = new Set(basePartners.map((name) => name.trim().toUpperCase()));
    const dataMap = new Map();

    tableData.forEach((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      if (key) {
        dataMap.set(key, row);
      }
    });

    const partnerRows = basePartners.map((name) => {
      const key = name.trim().toUpperCase();
      const existingRow = dataMap.get(key);
      if (existingRow) {
        return existingRow;
      }
      return {
        partnerName: name,
        od: 0,
        gl: 0,
        cd: 0,
        tbd: 0,
        dc: 0,
      };
    });

    const extraRows = tableData.filter((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      return key && !partnerKeys.has(key);
    });

    return [...partnerRows, ...extraRows];
  }, [basePartners, tableData]);

  const calculateGrandTotal = (rows) => {
    const totals = { od: 0, gl: 0, cd: 0, tbd: 0, dc: 0, total: 0 };

    rows.forEach((row) => {
      totals.od += toNumber(row.od);
      totals.gl += toNumber(row.gl);
      totals.cd += toNumber(row.cd);
      totals.tbd += toNumber(row.tbd);
      totals.dc += toNumber(row.dc);
      totals.total += calculateRowTotal(row);
    });

    return totals;
  };

  const grandTotal = useMemo(() => calculateGrandTotal(displayRows), [displayRows]);

  const handlePrint = () => {
    const printContent = document.getElementById('table-container').innerHTML;
    const printWindow = window.open('', '_blank');
    const partnersJoined = partnersList.join(', ') || '—';
    printWindow.document.write(`
      <html>
        <head>
          <title>Partnetwise Interest Meeting Data</title>
          <style>
            body { font-family: Arial, sans-serif; }
            h2, h4 { margin: 0 0 8px; }
            .meta { margin: 8px 0 16px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 16px; }
            th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
            th { background-color: #f4f4f4; font-weight: bold; }
            .font-bold { font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Partnerwise Interest Meeting Data</h2>
          <div class="meta">
            <h4>Date Range: ${startDate} to ${endDate}</h4>
           
          </div>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="container">
      <h2 className="title">Partner Meeting Data</h2>

      <div className="controls">
        <div className="date-input">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="date-input">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button onClick={() => fetchData()} className="fetch-button">Fetch Data</button>
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div id="table-container" className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner Name</th>
                <th>OD</th>
                <th>GL</th>
                <th>CD</th>
                <th>TBD</th>
                <th>DC</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, index) => (
                <tr key={index}>
                  <td>{row.partnerName || 'N/A'}</td>
                  <td>{toNumber(row.od).toFixed(2)}</td>
                  <td>{toNumber(row.gl).toFixed(2)}</td>
                  <td>{toNumber(row.cd).toFixed(2)}</td>
                  <td>{toNumber(row.tbd).toFixed(2)}</td>
                  <td>{toNumber(row.dc).toFixed(2)}</td>
                  <td>{calculateRowTotal(row).toFixed(2)}</td>
                </tr>
              ))}

              {/* Grand Total Row */}
              <tr className="font-bold">
                <td>Grand Total</td>
                <td>{grandTotal.od.toFixed(2)}</td>
                <td>{grandTotal.gl.toFixed(2)}</td>
                <td>{grandTotal.cd.toFixed(2)}</td>
                <td>{grandTotal.tbd.toFixed(2)}</td>
                <td>{grandTotal.dc.toFixed(2)}</td>
                <td>{grandTotal.total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <button onClick={handlePrint} className="print-button">Print Table</button>
    </div>
  );
};

export default PartnerMeetingDisplay;

